import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { trackSystemError } from '../alerting';

import { updateProfileFn } from '@/data/profile/use-profile.client';
import { safeLocalStorage } from '@/lib/safe-storage';

/**
 * Store for managing the globally selected GitHub installation across the app.
 * This is used by the InstallationSelect dropdown component to maintain
 * consistent installation selection state across different pages and components.
 * The selection is persisted in localStorage to maintain state across page refreshes.
 */
type InstallationStore = {
  /** The currently selected GitHub installation ID in the dropdown */
  selectedInstallationId: number | null;
  /** Update the selected installation ID */
  setSelectedInstallationId: (id: number | null) => void;
  /** Function to initialize the selected installation ID from the server */
  initializeFromServer: (serverInstallationId: number | null) => void;
};

export const useInstallationStore = create<InstallationStore>()(
  persist(
    (set, get) => ({
      selectedInstallationId: null,
      setSelectedInstallationId: (id: number | null) => {
        set({ selectedInstallationId: id });

        // Synchronize with server when changed by the user
        updateProfileFn({ selectedInstallationId: id }).catch((error) => {
          console.error('Failed to update selected installation ID on server:', error);
          trackSystemError(error, {
            type: 'update-profile',
            extra: {
              selectedInstallationId: id,
            },
          });
        });
      },
      initializeFromServer: (serverInstallationId: number | null) => {
        const currentId = get().selectedInstallationId;
        console.log('[InstallationStore] initializeFromServer called:', {
          serverInstallationId,
          currentSelectedInstallationId: currentId,
        });

        // Only set if there's no current selection
        // This prevents server values from overriding user selections
        if (currentId === null) {
          console.log(
            '[InstallationStore] Setting installation ID from server:',
            serverInstallationId,
          );
          set({ selectedInstallationId: serverInstallationId });
        } else {
          console.log(
            '[InstallationStore] Skipping initialization - user has already selected:',
            currentId,
          );
        }
      },
    }),
    {
      name: 'installation-storage',
      storage: createJSONStorage(() => safeLocalStorage),
    },
  ),
);
